"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import WaveApprovalStatusBar from "@/components/waves/approval/WaveApprovalStatusBar";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { AuthContext } from "@/components/auth/Auth";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { normalizeWaveLeaderboardSort } from "@/components/waves/leaderboard/header/WaveleaderboardSort";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import { getApprovedDropsCount } from "@/helpers/waves/approve-wave.helpers";
import { FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE, useWaveDecisions } from "@/hooks/waves/useWaveDecisions";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
interface CreateDropUiState {
  readonly waveId: string;
  readonly submissionExperience: WaveSubmissionExperience | null;
  readonly isApprovalVotingControlsLocked: boolean;
}
interface LeaderboardContentProps {
  readonly wave: ApiWave;
  readonly viewMode: LeaderboardViewMode;
  readonly sort: WaveDropsLeaderboardSort;
  readonly isMemesWave: boolean;
  readonly isVotingClosed: boolean;
  readonly isVotingControlsLocked: boolean;
  readonly curatedByGroupId: string | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly minPrice: number | undefined;
  readonly maxPrice: number | undefined;
  readonly priceCurrency: string | undefined;
  readonly onCreateDrop: (() => void) | undefined;
}
const isWaveLeaderboardSortPreference = (value: unknown, isApproveWave: boolean, isCurationWave: boolean): value is WaveDropsLeaderboardSort => value === WaveDropsLeaderboardSort.RANK || isApproveWave && value === WaveDropsLeaderboardSort.REALTIME_VOTE || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT || isCurationWave && value === WaveDropsLeaderboardSort.PRICE;
const stickyLeaderboardControlsClassName = "tw-sticky tw-top-0 tw-z-30 tw-flex-none tw-bg-black tw-py-4";
const staticLeaderboardControlsClassName = "tw-flex-none tw-bg-black tw-py-4";
interface LeaderboardControlsFrameProps {
  readonly isSticky: boolean;
  readonly children: React.ReactNode;
}
const LeaderboardControlsFrame: React.FC<LeaderboardControlsFrameProps> = t0 => {
  const $ = _c(3);
  const {
    isSticky,
    children
  } = t0;
  const t1 = isSticky ? stickyLeaderboardControlsClassName : staticLeaderboardControlsClassName;
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
interface ApproveListStickyLeaderboardControlsProps {
  readonly rootRef: React.RefObject<HTMLDivElement | null>;
  readonly children: React.ReactNode;
}
const ApproveListStickyLeaderboardControls: React.FC<ApproveListStickyLeaderboardControlsProps> = t0 => {
  const $ = _c(7);
  const {
    rootRef,
    children
  } = t0;
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== rootRef) {
    t1 = () => {
      const container = rootRef.current;
      const sentinel = sentinelRef.current;
      if (!container || !sentinel) {
        return;
      }
      if (typeof globalThis.IntersectionObserver === "undefined") {
        return;
      }
      const observer = new globalThis.IntersectionObserver(t3 => {
        const [entry] = t3;
        if (!entry) {
          return;
        }
        const rootTop = entry.rootBounds?.top ?? container.getBoundingClientRect().top;
        const hasScrolledPastSentinel = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
        setIsSticky(current => current === hasScrolledPastSentinel ? current : hasScrolledPastSentinel);
      }, {
        root: container,
        threshold: 0
      });
      observer.observe(sentinel);
      return () => observer.disconnect();
    };
    t2 = [rootRef];
    $[0] = rootRef;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div ref={sentinelRef} aria-hidden="true" data-testid="approval-controls-sticky-sentinel" className="tw-h-px tw-w-full tw-flex-none" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== isSticky) {
    t4 = <>{t3}<LeaderboardControlsFrame isSticky={isSticky}>{children}</LeaderboardControlsFrame></>;
    $[4] = children;
    $[5] = isSticky;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
const LeaderboardContent: React.FC<LeaderboardContentProps> = t0 => {
  const $ = _c(32);
  const {
    wave,
    viewMode,
    sort,
    isMemesWave,
    isVotingClosed,
    isVotingControlsLocked,
    curatedByGroupId,
    onDropClick,
    minPrice,
    maxPrice,
    priceCurrency,
    onCreateDrop
  } = t0;
  if (viewMode === "list") {
    let t1;
    if ($[0] !== curatedByGroupId || $[1] !== isVotingClosed || $[2] !== isVotingControlsLocked || $[3] !== maxPrice || $[4] !== minPrice || $[5] !== onCreateDrop || $[6] !== onDropClick || $[7] !== priceCurrency || $[8] !== sort || $[9] !== wave) {
      t1 = <WaveLeaderboardDrops wave={wave} sort={sort} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} curatedByGroupId={curatedByGroupId} onDropClick={onDropClick} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onCreateDrop={onCreateDrop} />;
      $[0] = curatedByGroupId;
      $[1] = isVotingClosed;
      $[2] = isVotingControlsLocked;
      $[3] = maxPrice;
      $[4] = minPrice;
      $[5] = onCreateDrop;
      $[6] = onDropClick;
      $[7] = priceCurrency;
      $[8] = sort;
      $[9] = wave;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    return t1;
  }
  if (!isMemesWave) {
    const t1 = viewMode === "grid" ? "compact" : "content_only";
    let t2;
    if ($[11] !== curatedByGroupId || $[12] !== isVotingClosed || $[13] !== isVotingControlsLocked || $[14] !== maxPrice || $[15] !== minPrice || $[16] !== onDropClick || $[17] !== priceCurrency || $[18] !== sort || $[19] !== t1 || $[20] !== wave) {
      t2 = <WaveLeaderboardGrid wave={wave} sort={sort} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} mode={t1} onDropClick={onDropClick} />;
      $[11] = curatedByGroupId;
      $[12] = isVotingClosed;
      $[13] = isVotingControlsLocked;
      $[14] = maxPrice;
      $[15] = minPrice;
      $[16] = onDropClick;
      $[17] = priceCurrency;
      $[18] = sort;
      $[19] = t1;
      $[20] = wave;
      $[21] = t2;
    } else {
      t2 = $[21];
    }
    return t2;
  }
  let t1;
  if ($[22] !== curatedByGroupId || $[23] !== isVotingClosed || $[24] !== isVotingControlsLocked || $[25] !== maxPrice || $[26] !== minPrice || $[27] !== onDropClick || $[28] !== priceCurrency || $[29] !== sort || $[30] !== wave) {
    t1 = <WaveLeaderboardGallery wave={wave} sort={sort} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onDropClick={onDropClick} />;
    $[22] = curatedByGroupId;
    $[23] = isVotingClosed;
    $[24] = isVotingControlsLocked;
    $[25] = maxPrice;
    $[26] = minPrice;
    $[27] = onDropClick;
    $[28] = priceCurrency;
    $[29] = sort;
    $[30] = wave;
    $[31] = t1;
  } else {
    t1 = $[31];
  }
  return t1;
};
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(129);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const leaderboardContainerRef = useRef(null);
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isApproveWave,
    isMemesWave,
    isCurationWave,
    isQuorumWave,
    participation
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const submissionExperience = t2;
  const mountedRef = useRef(true);
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      mountedRef.current = false;
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const [minPrice, setMinPrice] = useState(undefined);
  const [maxPrice, setMaxPrice] = useState(undefined);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t5 = Boolean(activeProfileProxy);
  let t6;
  if ($[7] !== isCurationWave || $[8] !== isLoggedIn || $[9] !== participation || $[10] !== t5) {
    t6 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t5,
      isCurationWave,
      participation
    });
    $[7] = isCurationWave;
    $[8] = isLoggedIn;
    $[9] = participation;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    canCreateDrop
  } = t6;
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  let t7;
  if ($[12] !== isApproveWave || $[13] !== isCurationWave) {
    t7 = value => isWaveLeaderboardSortPreference(value, isApproveWave, isCurationWave);
    $[12] = isApproveWave;
    $[13] = isCurationWave;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, t7);
  let t8;
  if ($[15] !== isApproveWave || $[16] !== sort || $[17] !== wave.wave.time_lock_ms) {
    t8 = normalizeWaveLeaderboardSort({
      isApproveWave,
      sort,
      timeLockMs: wave.wave.time_lock_ms
    });
    $[15] = isApproveWave;
    $[16] = sort;
    $[17] = wave.wave.time_lock_ms;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const effectiveSort = t8;
  let t9;
  if ($[19] !== isApproveWave || $[20] !== setSort || $[21] !== wave.wave.time_lock_ms) {
    t9 = nextSort => {
      setSort(normalizeWaveLeaderboardSort({
        isApproveWave,
        sort: nextSort,
        timeLockMs: wave.wave.time_lock_ms
      }));
    };
    $[19] = isApproveWave;
    $[20] = setSort;
    $[21] = wave.wave.time_lock_ms;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleSortChange = t9;
  const t10 = wave.wave.type !== ApiWaveType.Chat;
  let t11;
  if ($[23] !== t10 || $[24] !== wave.id) {
    t11 = {
      waveId: wave.id,
      enabled: t10
    };
    $[23] = t10;
    $[24] = wave.id;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const {
    data: t12,
    isLoading: isLoadingCurationGroups,
    isError: isCurationGroupsError
  } = useWaveCurations(t11);
  let t13;
  if ($[26] !== t12) {
    t13 = t12 === undefined ? [] : t12;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const curationGroups = t13;
  let t14;
  if ($[28] !== isApproveWave || $[29] !== wave) {
    t14 = isApproveWave && getApprovedDropsCount({
      wave
    }) === null;
    $[28] = isApproveWave;
    $[29] = wave;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const cannotDeriveApprovedCount = t14;
  const shouldLoadApprovalDecisionPoints = cannotDeriveApprovedCount;
  let t15;
  if ($[31] !== shouldLoadApprovalDecisionPoints || $[32] !== wave.id) {
    t15 = {
      waveId: wave.id,
      enabled: shouldLoadApprovalDecisionPoints,
      loadAllPages: true,
      pageSize: FULL_APPROVAL_WAVE_DECISIONS_PAGE_SIZE
    };
    $[31] = shouldLoadApprovalDecisionPoints;
    $[32] = wave.id;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const {
    decisionPoints: approvalDecisionPoints,
    hasLoadedAllPages: hasLoadedApprovalDecisionPoints,
    isLoadingAllPagesError: isApprovalDecisionPointsLoadError,
    refetch: refetchApprovalDecisionPoints,
    fetchNextPage: fetchNextApprovalDecisionPointsPage,
    hasNextPage: hasNextApprovalDecisionPointsPage
  } = useWaveDecisions(t15);
  let t16;
  if ($[34] !== fetchNextApprovalDecisionPointsPage || $[35] !== hasNextApprovalDecisionPointsPage || $[36] !== refetchApprovalDecisionPoints) {
    t16 = () => {
      if (hasNextApprovalDecisionPointsPage) {
        fetchNextApprovalDecisionPointsPage();
        return;
      }
      refetchApprovalDecisionPoints();
    };
    $[34] = fetchNextApprovalDecisionPointsPage;
    $[35] = hasNextApprovalDecisionPointsPage;
    $[36] = refetchApprovalDecisionPoints;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const retryApprovalDecisionPointsLoad = t16;
  let t17;
  if ($[38] !== approvalDecisionPoints || $[39] !== hasLoadedApprovalDecisionPoints || $[40] !== isApprovalDecisionPointsLoadError || $[41] !== retryApprovalDecisionPointsLoad || $[42] !== shouldLoadApprovalDecisionPoints) {
    t17 = shouldLoadApprovalDecisionPoints ? {
      decisionPoints: approvalDecisionPoints,
      areDecisionPointsComplete: hasLoadedApprovalDecisionPoints,
      isDecisionPointsLoadError: isApprovalDecisionPointsLoadError,
      onRetryDecisionPointsLoad: retryApprovalDecisionPointsLoad
    } : {};
    $[38] = approvalDecisionPoints;
    $[39] = hasLoadedApprovalDecisionPoints;
    $[40] = isApprovalDecisionPointsLoadError;
    $[41] = retryApprovalDecisionPointsLoad;
    $[42] = shouldLoadApprovalDecisionPoints;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t17 || $[45] !== wave) {
    t18 = {
      wave,
      ...t17
    };
    $[44] = t17;
    $[45] = wave;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const {
    approvedCount,
    closeStatus: approvalCloseStatus,
    isApprovalStatusError,
    isVotingClosed: isApprovalVotingClosed,
    isVotingControlsLocked: isApprovalVotingControlsLocked,
    retryApprovalStatus
  } = useApprovalWaveStatus(t18);
  const isApprovalCountError = Boolean(shouldLoadApprovalDecisionPoints && isApprovalDecisionPointsLoadError && approvedCount === null && !isApprovalStatusError);
  const retryApprovalCount = isApprovalCountError ? retryApprovalDecisionPointsLoad : null;
  const canOpenCreateDrop = canCreateDrop && !isApprovalVotingControlsLocked;
  let t19;
  if ($[47] !== isApprovalVotingControlsLocked || $[48] !== wave.id) {
    t19 = () => ({
      waveId: wave.id,
      submissionExperience: null,
      isApprovalVotingControlsLocked
    });
    $[47] = isApprovalVotingControlsLocked;
    $[48] = wave.id;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const [createDropUiState, setCreateDropUiState] = useState(t19);
  const activeCreateDropExperience = canOpenCreateDrop && createDropUiState.waveId === wave.id && createDropUiState.submissionExperience === submissionExperience && createDropUiState.isApprovalVotingControlsLocked === isApprovalVotingControlsLocked ? createDropUiState.submissionExperience : null;
  const showToggleableDropInput = activeCreateDropExperience !== null && activeCreateDropExperience !== WaveSubmissionExperience.MEMES_LEGACY && activeCreateDropExperience !== WaveSubmissionExperience.CURATION_LEGACY && activeCreateDropExperience !== WaveSubmissionExperience.QUORUM_PROPOSAL;
  let t20;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      if (!mountedRef.current) {
        return;
      }
      setCreateDropUiState(_temp);
    };
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const closeCreateDrop = t20;
  let t21;
  if ($[51] !== canOpenCreateDrop || $[52] !== isApprovalVotingControlsLocked || $[53] !== submissionExperience || $[54] !== wave.id) {
    t21 = () => {
      if (!mountedRef.current) {
        return;
      }
      if (!canOpenCreateDrop) {
        return;
      }
      setCreateDropUiState({
        waveId: wave.id,
        submissionExperience,
        isApprovalVotingControlsLocked
      });
    };
    $[51] = canOpenCreateDrop;
    $[52] = isApprovalVotingControlsLocked;
    $[53] = submissionExperience;
    $[54] = wave.id;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  const onCreateDrop = t21;
  let t22;
  if ($[56] !== searchParams) {
    t22 = searchParams.get("curation_id");
    $[56] = searchParams;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  const rawCuratedByGroupId = t22;
  const curationGroupIdSet = new Set(curationGroups.map(_temp2));
  let t23;
  bb0: {
    if (!rawCuratedByGroupId) {
      t23 = undefined;
      break bb0;
    }
    if (isCurationGroupsError) {
      t23 = undefined;
      break bb0;
    }
    if (isLoadingCurationGroups) {
      t23 = rawCuratedByGroupId;
      break bb0;
    }
    t23 = curationGroupIdSet.has(rawCuratedByGroupId) ? rawCuratedByGroupId : undefined;
  }
  const curatedByGroupId = t23;
  let t24;
  bb1: {
    const hasPriceFilter = typeof minPrice === "number" || typeof maxPrice === "number";
    if (isCurationWave && (hasPriceFilter || effectiveSort === WaveDropsLeaderboardSort.PRICE)) {
      t24 = "ETH";
      break bb1;
    }
    t24 = undefined;
  }
  const priceCurrency = t24;
  let t25;
  if ($[58] !== pathname || $[59] !== router || $[60] !== searchParams) {
    t25 = groupId => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (groupId) {
        nextParams.set("curation_id", groupId);
      } else {
        nextParams.delete("curation_id");
      }
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[58] = pathname;
    $[59] = router;
    $[60] = searchParams;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  const updateCurationGroupInUrl = t25;
  let t26;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = t27 => {
      const {
        minPrice: nextMinPrice,
        maxPrice: nextMaxPrice
      } = t27;
      setMinPrice(nextMinPrice);
      setMaxPrice(nextMaxPrice);
    };
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  const updatePriceRange = t26;
  let t27;
  bb2: {
    if (!isMemesWave) {
      t27 = viewMode;
      break bb2;
    }
    if (viewMode === "grid_content_only") {
      t27 = "grid";
      break bb2;
    }
    t27 = viewMode;
  }
  const effectiveViewMode = t27;
  const createDropAction = canOpenCreateDrop ? onCreateDrop : undefined;
  const shouldDelayApprovalControlsSticky = isApproveWave && effectiveViewMode === "list";
  let t28;
  if ($[63] !== setViewMode) {
    t28 = mode => setViewMode(mode);
    $[63] = setViewMode;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  const t29 = curatedByGroupId ?? null;
  const t30 = curationGroups.length > 0 ? updateCurationGroupInUrl : undefined;
  const t31 = isCurationWave ? updatePriceRange : undefined;
  let t32;
  if ($[65] !== createDropAction || $[66] !== curationGroups || $[67] !== effectiveSort || $[68] !== effectiveViewMode || $[69] !== handleSortChange || $[70] !== maxPrice || $[71] !== minPrice || $[72] !== t28 || $[73] !== t29 || $[74] !== t30 || $[75] !== t31 || $[76] !== wave) {
    t32 = <WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={effectiveSort} onViewModeChange={t28} onCreateDrop={createDropAction} onSortChange={handleSortChange} curationGroups={curationGroups} curatedByGroupId={t29} onCurationGroupChange={t30} minPrice={minPrice} maxPrice={maxPrice} onPriceRangeChange={t31} />;
    $[65] = createDropAction;
    $[66] = curationGroups;
    $[67] = effectiveSort;
    $[68] = effectiveViewMode;
    $[69] = handleSortChange;
    $[70] = maxPrice;
    $[71] = minPrice;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
    $[76] = wave;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  const leaderboardControls = t32;
  let t33;
  if ($[78] !== approvalCloseStatus || $[79] !== approvedCount || $[80] !== isApprovalCountError || $[81] !== isApprovalStatusError || $[82] !== isApproveWave || $[83] !== retryApprovalCount || $[84] !== retryApprovalStatus || $[85] !== wave) {
    t33 = isApproveWave ? <WaveApprovalStatusBar approvedCount={approvedCount} closeStatus={approvalCloseStatus} isApprovalCountError={isApprovalCountError} isApprovalStatusError={isApprovalStatusError} retryApprovalCount={retryApprovalCount} retryApprovalStatus={retryApprovalStatus} wave={wave} /> : <WaveLeaderboardTime wave={wave} />;
    $[78] = approvalCloseStatus;
    $[79] = approvedCount;
    $[80] = isApprovalCountError;
    $[81] = isApprovalStatusError;
    $[82] = isApproveWave;
    $[83] = retryApprovalCount;
    $[84] = retryApprovalStatus;
    $[85] = wave;
    $[86] = t33;
  } else {
    t33 = $[86];
  }
  let t34;
  if ($[87] !== leaderboardControls || $[88] !== shouldDelayApprovalControlsSticky || $[89] !== wave.id) {
    t34 = shouldDelayApprovalControlsSticky ? <ApproveListStickyLeaderboardControls key={`${wave.id}:list`} rootRef={leaderboardContainerRef}>{leaderboardControls}</ApproveListStickyLeaderboardControls> : <LeaderboardControlsFrame isSticky={true}>{leaderboardControls}</LeaderboardControlsFrame>;
    $[87] = leaderboardControls;
    $[88] = shouldDelayApprovalControlsSticky;
    $[89] = wave.id;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] !== showToggleableDropInput || $[92] !== wave) {
    t35 = showToggleableDropInput && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={closeCreateDrop} onSuccess={closeCreateDrop} /></motion.div>;
    $[91] = showToggleableDropInput;
    $[92] = wave;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  let t36;
  if ($[94] !== t35) {
    t36 = <AnimatePresence>{t35}</AnimatePresence>;
    $[94] = t35;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  let t37;
  if ($[96] !== activeCreateDropExperience || $[97] !== wave) {
    t37 = activeCreateDropExperience === WaveSubmissionExperience.MEMES_LEGACY && <MemesArtSubmissionModal isOpen={true} wave={wave} onClose={closeCreateDrop} />;
    $[96] = activeCreateDropExperience;
    $[97] = wave;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  let t38;
  if ($[99] !== activeCreateDropExperience || $[100] !== wave) {
    t38 = activeCreateDropExperience === WaveSubmissionExperience.CURATION_LEGACY && <WaveLeaderboardCurationDropModal isOpen={true} wave={wave} onClose={closeCreateDrop} />;
    $[99] = activeCreateDropExperience;
    $[100] = wave;
    $[101] = t38;
  } else {
    t38 = $[101];
  }
  let t39;
  if ($[102] !== activeCreateDropExperience || $[103] !== wave) {
    t39 = activeCreateDropExperience === WaveSubmissionExperience.QUORUM_PROPOSAL && <WaveDropCreate wave={wave} onCancel={closeCreateDrop} onSuccess={closeCreateDrop} />;
    $[102] = activeCreateDropExperience;
    $[103] = wave;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  let t40;
  if ($[105] !== createDropAction || $[106] !== curatedByGroupId || $[107] !== effectiveSort || $[108] !== effectiveViewMode || $[109] !== isApprovalVotingClosed || $[110] !== isApprovalVotingControlsLocked || $[111] !== isMemesWave || $[112] !== maxPrice || $[113] !== minPrice || $[114] !== onDropClick || $[115] !== priceCurrency || $[116] !== wave) {
    t40 = <LeaderboardContent wave={wave} viewMode={effectiveViewMode} sort={effectiveSort} isMemesWave={isMemesWave} isVotingClosed={isApprovalVotingClosed} isVotingControlsLocked={isApprovalVotingControlsLocked} curatedByGroupId={curatedByGroupId} onDropClick={onDropClick} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onCreateDrop={createDropAction} />;
    $[105] = createDropAction;
    $[106] = curatedByGroupId;
    $[107] = effectiveSort;
    $[108] = effectiveViewMode;
    $[109] = isApprovalVotingClosed;
    $[110] = isApprovalVotingControlsLocked;
    $[111] = isMemesWave;
    $[112] = maxPrice;
    $[113] = minPrice;
    $[114] = onDropClick;
    $[115] = priceCurrency;
    $[116] = wave;
    $[117] = t40;
  } else {
    t40 = $[117];
  }
  let t41;
  if ($[118] !== t36 || $[119] !== t37 || $[120] !== t38 || $[121] !== t39 || $[122] !== t40) {
    t41 = <div className="tw-min-w-0 tw-pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]">{t36}{t37}{t38}{t39}{t40}</div>;
    $[118] = t36;
    $[119] = t37;
    $[120] = t38;
    $[121] = t39;
    $[122] = t40;
    $[123] = t41;
  } else {
    t41 = $[123];
  }
  let t42;
  if ($[124] !== leaderboardViewStyle || $[125] !== t33 || $[126] !== t34 || $[127] !== t41) {
    t42 = <div ref={leaderboardContainerRef} className="tw-w-full tw-min-w-0 tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t33}{t34}{t41}</div>;
    $[124] = leaderboardViewStyle;
    $[125] = t33;
    $[126] = t34;
    $[127] = t41;
    $[128] = t42;
  } else {
    t42 = $[128];
  }
  return t42;
};
export default MyStreamWaveLeaderboard;
function _temp(current) {
  if (current.submissionExperience === null) {
    return current;
  }
  return {
    ...current,
    submissionExperience: null
  };
}
function _temp2(group) {
  return group.id;
}